import React, {useState} from 'react';
import Coffee from '../../assets/icons/Clipboard.svg';
import User from '../../assets/icons/User.svg';
import Users from '../../assets/icons/Users.svg';
import Tool from '../../assets/icons/Tool.svg';
import dayjs from 'dayjs';

import apiClient from "../../context/apiClient";
import useRules from '../../hooks/useRules';

const roleIconMap = {
    LEADER: User,
    INSTRUCTOR: Users,
    MECHANIC: Tool,
    GUEST_MANAGER: Coffee,
};

const LeistungspaketEnum = {
    FS5: "FS5",
    FS5HT: "FS5HT",
    FSTA: "FSTA",
    FSA: "FSA",
    FSTaxi: "FSTaxi",
    FSTour: "FSTour",
    FSTE: "FSTE",
    FSTtT: "FSTtT",
    FSK: "FSK",
    FSKHT: "FSKHT",
    FSMod: "FSMod",
    FSReise: "FSReise",
    FSReiseH: "FSReiseH",
    TB: "TB",
    TBHT: "TBHT",
    TBA: "TBA",
    TBReise: "TBReise",
    TBReiseH: "TBReiseH",
    FZ6: "FZ6",
    FZ6HT: "FZ6HT",
    FZ6A: "FZ6A",
    FZ6AHT: "FZ6AHT",
    FZ6AVorNach: "FZ6AVorNach",
    FZ6AHTVorNach: "FZ6AHTVorNach",
    FZ6AReise: "FZ6AReise",
    FZ6AReiseH: "FZ6AReiseH",
    FS5VorNach: "FS5VorNach",
    FS5HTVorNach: "FS5HTVorNach",
    TBVorv: "TBVorv",
    TBHTVorNach: "TBHTVorNach",
};


const roleOrder = ['LEADER', 'INSTRUCTOR', 'MECHANIC', 'GUEST_MANAGER'];

const EventLeistungspaketeCard = ({event, onUpdate, onRotePaketeUpdate}) => {
    const { excludedSet } = useRules();
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const renderPaketList = (pakete) => {
        if (!pakete || pakete.length === 0) return 'Keine Pakete';

        const paketCount = pakete.reduce((acc, name) => {
            acc[name] = (acc[name] || 0) + 1;
            return acc;
        }, {});

        return Object.entries(paketCount)
            .map(([name, count]) => `${name} (${count})`)
            .join(', ');
    };

    const formatDateRange = (start, end) => {
        const sameDay = dayjs(start).isSame(end, 'day');

        if (sameDay) {
            return `${dayjs(start).format('DD.MM.YYYY, HH:mm')} – ${dayjs(end).format('HH:mm')}`;
        } else {
            return `${dayjs(start).format('DD.MM.YYYY, HH:mm')} – ${dayjs(end).format('DD.MM.YYYY, HH:mm')}`;
        }
    };

    const leader = event.leader
        ? {
            ...event.leader,
            role: 'LEADER',
            pakete: event.leistungspakete[event.leader.id] || [],
        }
        : null;

    const allEmployees = event.employeeList.map((emp) => ({
        ...emp,
        pakete: event.leistungspakete[emp.id] || [],
    }));

    const grouped = {
        LEADER: [],
        INSTRUCTOR: [],
        MECHANIC: [],
        GUEST_MANAGER: [],
    };

    if (leader) grouped.LEADER.push(leader);
    for (const emp of allEmployees) {
        grouped[emp.role]?.push(emp);
    }

    return (
        <div className="flex w-full grid grid-cols-5 bg-white shadow rounded-lg">
            <div className="flex flex-col justify-center items-start px-3 py-2 border-r">
                <h3 className="text-sm font-medium text-gray-800 truncate max-w-full">
                    {event.name}
                </h3>
                <p className="text-xs text-gray-500">
                    {formatDateRange(event.timeframe.startTime, event.timeframe.endTime)}
                </p>
            </div>

            {roleOrder.map((role, index) => (
                <div
                    key={role}
                    className={`px-4 py-2 flex flex-col gap-1 ${
                        index < roleOrder.length - 1 ? 'border-r' : ''
                    }`}
                >
                    {grouped[role].map((emp, idx) => (
                        <div key={emp.id || idx} className="flex items-center gap-2 text-sm min-w-0">
                            <img
                                src={roleIconMap[role]}
                                alt={role}
                                className="w-4 h-4 flex-shrink-0"
                            />

                            {/* Flex-Container für Name + Pakete */}
                            <div className="flex flex-1 min-w-0 gap-2 items-center">
                                {/* Name – als "peer", um Hover-Zustand an Pakete weiterzugeben */}
                                <span
                                    title={`${emp.firstName} ${emp.lastName}`}
                                    className={`
                                        truncate hover:flex-[1] flex-[1] min-w-0 peer
                                        transition-all duration-150 cursor-pointer
                                        ${excludedSet.has(`${emp.firstName}, ${emp.lastName}`) ? 'text-red-600' : 'text-gray-700'}
                                    `}
                                                        >
                                    {`${emp.firstName || ''} ${emp.lastName || ''}`.trim()}
                                </span>

                                {/* Pakete – reagieren auf Hover des "peer"-Elements (Name) */}
                                <button
                                    className={`
                                        text-left whitespace-nowrap
                                        overflow-hidden text-ellipsis min-w-0
                                        transition-all duration-150
                                        peer-hover:flex-[1] flex-[3]
                                        ${excludedSet.has(`${emp.firstName}, ${emp.lastName}`) ? 'text-red-600' : 'text-gray-600'}
                                    `}
                                    onClick={() => {
                                        setSelectedEmployee(emp);
                                        setShowModal(true);
                                    }}
                                >
                                    {renderPaketList(emp.pakete)}
                                </button>
                            </div>
                        </div>

                    ))}
                </div>
            ))}
            {showModal && selectedEmployee && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                    <div
                        className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200 animate-scaleIn transition-all">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">
                            Pakete bearbeiten – {selectedEmployee.firstName} {selectedEmployee.lastName}
                        </h2>
                        <div className="max-h-[70vh] overflow-y-auto pr-1 grid grid-cols-2 gap-x-6 gap-y-2">
                            {Object.values(LeistungspaketEnum).map((pkg) => {
                                const currentCount = selectedEmployee.pakete?.filter(p => p === pkg).length || 0;
                                return (
                                    <div key={pkg} className="flex items-center justify-between text-sm text-gray-700">
                                        <span>{pkg}</span>
                                        <input
                                            type="number"
                                            min={0}
                                            value={currentCount}
                                            onChange={(e) => {
                                                const newCount = parseInt(e.target.value) || 0;
                                                const updatedList = Array(newCount).fill(pkg);
                                                const rest = (selectedEmployee.pakete || []).filter(p => p !== pkg);
                                                setSelectedEmployee(prev => ({
                                                    ...prev,
                                                    pakete: [...rest, ...updatedList],
                                                }));
                                            }}
                                            className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300 transition"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        // Build updated leistungspakete map
                                        const updatedLeistungspakete = {
                                            ...(event.leistungspakete || {}),
                                            [selectedEmployee.id]: selectedEmployee.pakete,
                                        };

                                        // Build the full payload based on current event + updated pakete
                                        const payload = {
                                            id: event.id,
                                            name: event.name,
                                            timeframe: event.timeframe,
                                            location: event.location,
                                            eventType: event.eventType,
                                            leader: event.leader,
                                            employeeList: event.employeeList,
                                            numberLeader: event.numberLeader,
                                            numberInstructors: event.numberInstructors,
                                            numberMechanics: event.numberMechanics,
                                            numberGuestManagers: event.numberGuestManagers,
                                            qualifications: event.qualifications,
                                            languages: event.languages,
                                            leistungspakete: updatedLeistungspakete,
                                            eventStatus: event.eventStatus,
                                        };

                                        await apiClient.put(`/api/events/updateEvent/${event.id}`, payload);

                                        if (onRotePaketeUpdate) {
                                            const isExcluded = excludedSet.has(`${selectedEmployee.firstName}, ${selectedEmployee.lastName}`);
                                            if (isExcluded) {
                                                onRotePaketeUpdate(selectedEmployee.role, selectedEmployee.pakete);
                                            }
                                        }

                                        console.log("✅ Event updated successfully");
                                        setShowModal(false);

                                        if (onUpdate) {
                                            onUpdate();
                                        }
                                    } catch (err) {
                                        console.error("❌ Failed to update event:", err);
                                        alert("Fehler beim Speichern der Pakete.");
                                    }
                                }}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-500 transition"
                            >
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventLeistungspaketeCard;
